import { useState, useMemo } from "react";
import {
    MaterialReactTable,
    useMaterialReactTable,
    type MRT_ColumnDef,
} from "material-react-table";
import {
    Button,
    Grid,
    Paper,
    Typography,
    TextField,
    Box
} from "@mui/material"
import { useListaDeduplicacaoService, useRecortesExtracaoService } from "../../../../../../../use-cases/geo-veles"
import { useAppSelector } from "../../../../../../hooks/store-hooks"
import { useEffect } from "react"
// Icons
import DeleteIcon from '@mui/icons-material/Delete'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import SearchIcon from '@mui/icons-material/Search';
import { IListaDeduplicacao } from "../../../../../../../application/models/geo-veles/IListaDeduplicacao";

// Interface para os dados de CPF
interface CpfData {
    id: number;
    cpf: string;
}

// Interface para listas filtradas
interface ListaFiltrada {
    lista: IListaDeduplicacao;
    cpfsFiltrados: CpfData[];
    nomeCorresponde: boolean;
}

// Componente individual para cada lista
const ListaDeduplicacaoVirtualTable = ({
    lista,
    cpfsFiltrados,
    mostraTodosCpfs
}: {
    lista: IListaDeduplicacao;
    cpfsFiltrados: CpfData[];
    mostraTodosCpfs: boolean;
}) => {
    const columns = useMemo<MRT_ColumnDef<CpfData>[]>(
        () => [
            {
                accessorKey: 'cpf',
                header: 'CPF',
                size: 200,
                Cell: ({ cell }) => (
                    <Typography
                        variant="body2"
                        sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.875rem'
                        }}
                    >
                        {cell.getValue<string>()}
                    </Typography>
                ),
            },
        ],
        [],
    );

    const table = useMaterialReactTable({
        columns,
        data: cpfsFiltrados,
        enablePagination: false,
        enableRowNumbers: false,
        enableToolbarInternalActions: false,
        enableBottomToolbar: false,
        enableTopToolbar: false,
        enableColumnActions: false,
        enableColumnFilters: false,
        enableSorting: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        // Configurações de virtualização para performance
        enableRowVirtualization: true,
        enableStickyHeader: true,
        // Ajustes de layout responsivo
        layoutMode: 'grid',
        // Configurações de desempenho
        defaultDisplayColumn: {
            enableResizing: false,
        },
        muiTableContainerProps: {
            sx: {
                maxHeight: '400px',
                minHeight: '200px',
            },
        },
        muiTableBodyRowProps: {
            sx: {
                height: '40px',
            },
        },
        muiTableBodyCellProps: {
            sx: {
                padding: '8px 16px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
    });

    const totalCpfs = useMemo(() => {
        return ((lista as any).CPFs || []).length;
    }, [lista]);

    return (
        <Paper elevation={1} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, flexShrink: 0 }}>
                <FormatListBulletedIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
                    {lista.Nome || `Lista ${lista.IdVelesGeoListaDeduplicacao}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {mostraTodosCpfs
                        ? `${totalCpfs} CPF(s)`
                        : `${cpfsFiltrados.length} de ${totalCpfs} CPF(s)`
                    }
                </Typography>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0 }}>
                <MaterialReactTable table={table} />
            </Box>
        </Paper>
    );
};

export const RecorteListasDeduplicacaoFilterPreview = () => {
    // State - busca geral única
    const [searchTerm, setSearchTerm] = useState("");

    const { createRecorteExtracaoForm } = useAppSelector((state) => state.recorteExtracao)
    const { listasDeduplicacao } = useAppSelector((state) => state.listaDeduplicacao)

    // Hooks
    const { removeCreateRecorteFormFiltros } = useRecortesExtracaoService()
    const { setupListaDeduplicacao } = useListaDeduplicacaoService()

    const hasListasDeduplicacao = useMemo(() => {
        const listasDoRedux = createRecorteExtracaoForm.fields.Filtros?.listasDeduplicacao;
        return listasDoRedux && listasDoRedux.length > 0;
    }, [createRecorteExtracaoForm.fields.Filtros?.listasDeduplicacao]);

    // Busca as listas completas baseado nos IDs selecionados
    const listasCarregadas = useMemo(() => {
        if (!hasListasDeduplicacao) return [];

        const listasDoRedux = createRecorteExtracaoForm.fields.Filtros?.listasDeduplicacao;

        return listasDeduplicacao.filter((lista: IListaDeduplicacao) =>
            listasDoRedux?.includes(lista.IdVelesGeoListaDeduplicacao)
        );
    }, [hasListasDeduplicacao, createRecorteExtracaoForm.fields.Filtros?.listasDeduplicacao, listasDeduplicacao]);

    // Filtra todas as listas baseado no termo de busca geral
    const listasFiltradas = useMemo((): ListaFiltrada[] => {
        if (!searchTerm.trim()) {
            return listasCarregadas.map((lista: IListaDeduplicacao) => {
                const cpfs = ((lista as any).CPFs || []).map((cpf: string, index: number) => ({
                    id: index,
                    cpf,
                }));
                return {
                    lista,
                    cpfsFiltrados: cpfs,
                    nomeCorresponde: true,
                };
            });
        }

        const term = searchTerm.toLowerCase();
        const termAsNumber = !isNaN(Number(term)) ? Number(term) : null;

        return listasCarregadas
            .map((lista: IListaDeduplicacao) => {
                const nomeCorresponde = lista.Nome?.toLowerCase().includes(term) ?? false;

                const todosCpfs = ((lista as any).CPFs || []).map((cpf: string, index: number) => ({
                    id: index,
                    cpf,
                }));

                const cpfsFiltrados = todosCpfs.filter((item: CpfData) => {
                    if (termAsNumber !== null) {
                        const cpfNumerico = item.cpf.replace(/\D/g, '');
                        return cpfNumerico.includes(searchTerm);
                    }
                    return item.cpf.toLowerCase().includes(term);
                });

                return { lista, cpfsFiltrados, nomeCorresponde };
            })
            .filter((listaFiltrada: ListaFiltrada) =>
                listaFiltrada.nomeCorresponde || listaFiltrada.cpfsFiltrados.length > 0
            );
    }, [listasCarregadas, searchTerm]);

    const handleRemoveFilter = () => {
        removeCreateRecorteFormFiltros({ filtro: 'listasDeduplicacao' });
        setSearchTerm("");
    };

    // Use Effect
    useEffect(() => {
        setupListaDeduplicacao()
    }, [setupListaDeduplicacao])

    if (!hasListasDeduplicacao) {
        return null;
    }

    // Calcula o layout responsivo baseado na quantidade de listas
    const getGridLayout = () => {
        const count = listasFiltradas.length;
        if (count === 0) return { xs: 12 };
        if (count === 1) return { xs: 12 };
        return { xs: 12, md: 6 }; // Máximo 2 por linha
    };

    return (
        <Grid item container xs={12} spacing={2} mb={2} p={2}>
            <Paper elevation={3} sx={{ padding: 2, width: "100%" }}>
                <Grid item xs={12} container justifyContent="space-between" alignItems="center" my={1}>
                    <Grid item xs={6}>
                        <Typography
                            variant="body1"
                            color="primary"
                            fontWeight="bold"
                            textTransform="uppercase"
                        >
                            Listas de Deduplicação ({listasCarregadas.length})
                        </Typography>
                    </Grid>
                    <Grid item xs={6} container justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            startIcon={<DeleteIcon />}
                            onClick={handleRemoveFilter}
                        >
                            Remover filtro
                        </Button>
                    </Grid>
                </Grid>

                {/* Barra de busca GERAL única */}
                <TextField
                    size="small"
                    placeholder="Pesquisar por nome da lista ou CPF em todas as listas..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    fullWidth
                    sx={{ mb: 3 }}
                    InputProps={{
                        startAdornment: (
                            <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                        ),
                    }}
                />

                {/* Grid responsivo com listas lado a lado */}
                <Grid container spacing={2}>
                    {listasFiltradas.length > 0 ? (
                        listasFiltradas.map((listaFiltrada: ListaFiltrada) => (
                            <Grid item {...getGridLayout()} key={listaFiltrada.lista.IdVelesGeoListaDeduplicacao.toString()}>
                                <ListaDeduplicacaoVirtualTable
                                    lista={listaFiltrada.lista}
                                    cpfsFiltrados={listaFiltrada.cpfsFiltrados}
                                    mostraTodosCpfs={!searchTerm.trim()}
                                />
                            </Grid>
                        ))
                    ) : (
                        <Grid item xs={12}>
                            <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body1" color="text.secondary">
                                    Nenhuma lista ou CPF encontrado para "{searchTerm}"
                                </Typography>
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            </Paper>
        </Grid>
    );
};